import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function FiveMinuteRule() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="The 5-Minute Rule: Why Roofing Companies Win (or Lose) Jobs Before Lunch"
            date="March 17, 2026"
            readTime="6 MIN"
            heroImage="/images/blog/five-minute-rule-hero.jpg"
            description="The job went to someone else. Not because they were cheaper. Because they called first. There's a rule in sales that explains exactly what happened — and why most roofing companies keep losing to it."
        >
            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                The job went to someone else. Not because they were cheaper. Because they called first.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                She submitted the contact form at 11:47 on a Tuesday morning. Three contractors got the same notification.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The first one called back in 4 minutes. He had her scheduled for a Thursday inspection before you even saw the lead.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                You got to it at 2:30. She was polite. "We already found someone, but thank you."
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That wasn't bad luck. That was a pattern.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The rule nobody told you about</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Researchers at MIT ran a large-scale study on lead response times. The finding: contact a lead within 5 minutes of their inquiry and you're 100 times more likely to actually reach them than if you wait 30 minutes. Close rate jumps 21x compared to responding after an hour.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's across industries. Roofing is harder.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A homeowner who just walked outside and saw hail damage on their roof is not browsing options the way they'd shop for a couch. Their ceiling might be leaking. Their insurance adjuster is already scheduled. They're scared, they have a timeline, and they need someone to tell them what to do next. They called two other contractors before they called you. The first one who gives them a clear answer gets the job.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The window isn't 5 minutes. It's less. And it closes fast.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">Why you can't hit it manually</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                During a normal week, a 4-crew roofing company gets maybe 15 inbound contacts. That's manageable. The office handles it, callbacks happen same-day, nothing falls through the cracks.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                During a hail event: 40 contacts in 24 hours. 60 if the storm is big. And they don't arrive during business hours. They come in at 7 PM when homeowners finally walk around their yard.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                17 voicemails. 9 form submissions. 6 texts to the personal number. A few DMs if you have a social profile.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                One person at the desk. Handling the phone. Scheduling crews. Ordering materials. Coordinating inspections. During the busiest week of the year.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                This isn't a motivation problem. It's an architecture problem. The 5-minute rule assumes a system that can respond in 5 minutes. A human doing 12 jobs at once during a surge cannot do that — no matter how hard they try.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                The 5-minute rule doesn't require more hustle. It requires a different system.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What it looks like when the system works</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The contractors consistently hitting the 5-minute rule aren't doing it with faster fingers. They've built something that works while they're on a roof.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A lead comes in at 11:47 AM. An automated response goes out at 11:49: the homeowner's name, your company name, a scheduling link, a clear next step. No one touched it. No one typed it.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                You get a summary notification when you come off the roof: new lead, auto-response sent, they've picked a Thursday inspection slot. When you have a break, you make the personal follow-up call. The appointment is already booked. The relationship is already started.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The homeowner, who submitted a form 4 minutes ago, already has a confirmation in her inbox and feels like someone's on it. When your competitor calls at 2:30, she tells them she's already set.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's not technology magic. It's a response loop that doesn't require a human to be watching the inbox at the exact right moment.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The math on a surge weekend</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Say a hail event hits your market on a Thursday. 40 homeowners with damage contact roofing companies in the next 24 hours. Your area has 8 active contractors competing for the same leads.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Without a response system: you reach maybe half of what comes in, because the rest go unanswered during hours you're not watching the phone. Of what you reach, a solid closer converts 40 to 50 percent. Call it 8 jobs. At $10,000 average: $80,000.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                With a system hitting the 5-minute rule: you capture 35 of those 40 contacts instead of 20. Your conversion holds at 45 percent. That's 15 jobs. $150,000.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                $70,000 difference. One storm. Same crews. Same closer.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The only variable is whether the system answered before the homeowner moved on.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What's possible by end of spring</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Hail season runs March through June, with the biggest event concentration in April and May. We're in the early part of it right now. March 10, Central Texas already took a hit.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                If you fix this before the next storm hits your market, every event becomes structured intake instead of controlled chaos. The 5-minute rule becomes something you hit by default. Compound that across 8 to 12 storm events in a season and the math gets very large.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The contractors who own spring aren't the ones with the best trucks. They're the ones who called back at 11:49.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                You could be hitting that number by Friday.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <div className="text-center my-12">
                <a
                    href="https://roofclaw.com"
                    className="inline-block bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-bold px-8 py-4 rounded-xl transition-colors text-lg"
                >
                    Book a 20-minute call
                </a>
            </div>

            <p className="text-[#F0EFF4]/60 text-base leading-relaxed">
                RoofClaw is an AI operations agent built for roofing companies. Delivered, configured, and running in days. No IT department required.
            </p>
        </BlogLayout>
    );
}
